// eslint-disable-next-line no-restricted-imports
import Column from 'components/Column'
import { defaultChainId, isSupportedChain } from 'config/chains'
import { REFETCH_POLICY_FAST } from 'config/RefetchPolicies'
import { PRE_TGE_XKDK_ADDRESSES, XKDK_ADDRESSES } from 'constants/addresses'
import { useToken } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import useSelectChain from 'hooks/useSelectChain'
import { useTokenBalance } from 'lib/hooks/useCurrencyBalance'
import { darken } from 'polished'
import { useMemo } from 'react'
import { AlertTriangle } from 'react-feather'
import { FaWallet } from 'react-icons/fa'
import styled, { css } from 'styled-components'
import { formatAmount } from 'utils/formatDollarAmt'

import useENSName from '../../hooks/useENSName'
import { useToggleWalletModal } from '../../state/application/hooks'
import { isTransactionRecent, useAllTransactions } from '../../state/transactions/hooks'
import { TransactionDetails } from '../../state/transactions/types'
import { shortenAddress } from '../../utils'
import { ButtonSecondary } from '../Button'
import Loader from '../Loader'
import { RowBetween } from '../Row'
import WalletModal from '../WalletModal'

const SHOW_KDK_BALANCE_SECTION = false

const Web3StatusGeneric = styled(ButtonSecondary)`
  display: flex;
  height: 40px;
  padding: 8px 20px 8px 0;
  justify-content: center;
  align-items: center;
  gap: 8px;

  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  background: ${({ theme }) => theme.cardPrimary};

  font-weight: 500;

  color: ${({ theme }) => theme.textPrimary};
  text-align: center;
  /* link/link-medium */
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;

  ${({ theme }) => theme.flexRowNoWrap}
  width: 100%;
  cursor: pointer;
  user-select: none;
  :focus {
    outline: none;
  }
`

const Web3StatusConnect = styled(Web3StatusGeneric)<{ faded?: boolean }>`
  padding-left: 20px;
  :hover,
  :focus {
    border: 1px solid ${({ theme }) => darken(0.05, theme.cardBorderPrimary)};
    color: ${({ theme }) => theme.textPrimary};
  }

  ${({ faded }) =>
    faded &&
    css`
      background-color: ${({ theme }) => theme.cardPrimary};
      border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
      color: ${({ theme }) => theme.textPrimary};

      :hover,
      :focus {
        border: 1px solid ${({ theme }) => darken(0.05, theme.cardBorderPrimary)};
        color: ${({ theme }) => darken(0.05, theme.textPrimary)};
      }
    `}
`

const Web3StatusConnected = styled(Web3StatusGeneric)<{ pending?: boolean }>`
  padding-left: ${({ pending }) => (pending ? `20px` : `${SHOW_KDK_BALANCE_SECTION ? '0' : '20'}px`)};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  // padding: 8px 21px;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.buttonBorder};

  min-width: 0;
  max-width: 280px;

  color: ${({ pending, theme }) => (pending ? theme.textSecondary : theme.textPrimary)};

  :hover,
  :focus {
    border: 1px solid ${({ theme }) => darken(0.05, theme.cardBorderPrimary)};
    background: ${({ theme }) => darken(0.05, theme.backgroundSurface)};

    :focus {
      border: 1px solid
        ${({ pending, theme }) =>
          pending ? darken(0.1, theme.deprecated_primary1) : darken(0.1, theme.deprecated_bg2)};
    }
  }
`

const Text = styled.p`
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: fit-content;
  font-weight: 500;

  color: ${({ theme }) => theme.textPrimary};
  text-align: center;
  /* link/link-medium */
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
`

const NetworkIcon = styled(AlertTriangle)`
  margin-left: 0.25rem;
  margin-right: 0.5rem;
  width: 16px;
  height: 16px;
`

const ConnectedContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
`

const TokenBalanceSection = styled.div<{ claimStyle?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  gap: 4px;
  background: ${({ theme }) => theme.backgroundModule};
  border-radius: 4px;
  width: 90px;
  flex-shrink: 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 4px;
    padding: 2px;
    background: conic-gradient(
      from var(--angle),
      transparent 0%,
      ${({ theme }) => theme.accentSuccess} 10%,
      ${({ theme }) => theme.accentSuccess}cc 15%,
      transparent 50%,
      transparent 100%
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    animation: rotate 2s linear infinite;
    filter: drop-shadow(0 0 16px ${({ theme }) => theme.accentSuccess}80);

    display: ${({ claimStyle }) => (claimStyle ? 'block' : 'none')};
  }

  border-right: 1px solid ${({ theme }) => theme.cardBorderSecondary};

  @property --angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }

  @keyframes rotate {
    to {
      --angle: 360deg;
    }
  }
`

const BalanceAmount = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
`

const ClaimBalanceAmount = styled.span<{ glow?: boolean }>`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.accentSuccess};
  white-space: nowrap;

  ${({ glow }) =>
    glow &&
    css`
      text-shadow: 0 0 5px ${({ theme }) => theme.accentSuccess}50, 0 0 10px ${({ theme }) => theme.accentSuccess}30,
        0 0 20px ${({ theme }) => theme.accentSuccess}15;
    `}
`

const TokenSymbol = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};
`

const WalletSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
`

const WalletIcon = styled(FaWallet as unknown as React.ComponentType<any>)`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.textSecondary};
  flex-shrink: 0;
`

const AddressText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  min-width: 0;
  flex: 1;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    max-width: 80px;
  `};

  ${({ theme }) => theme.mediaWidth.upToMedium`
    max-width: 120px;
  `};
`

// we want the latest one to come first, so return negative if a is after b
function newTransactionsFirst(a: TransactionDetails, b: TransactionDetails) {
  return b.addedTime - a.addedTime
}

const formatDisplayName = (ensName: string | null | undefined, address: string): string => {
  if (!ensName) {
    return shortenAddress(address)
  }

  return ensName
}

function KdkBalanceSection() {
  const { account, chainId } = useConnection()

  const xkdkAddress = XKDK_ADDRESSES[chainId]
  const xKDK = useToken(xkdkAddress)

  const preXkdkAddress = PRE_TGE_XKDK_ADDRESSES[chainId]
  const preXKDK = useToken(preXkdkAddress)

  const kdkBalance = useTokenBalance(account, xKDK ?? undefined, REFETCH_POLICY_FAST)
  const preXkdkBalance = useTokenBalance(account, preXKDK ?? undefined, REFETCH_POLICY_FAST)

  const showPreXkdkBalance = preXkdkBalance?.greaterThan(0)
  return showPreXkdkBalance ? (
    <TokenBalanceSection claimStyle>
      <Column>
        <ClaimBalanceAmount glow>
          +{!preXkdkBalance ? 0 : formatAmount(preXkdkBalance.toExact())} xKDK
        </ClaimBalanceAmount>
      </Column>
    </TokenBalanceSection>
  ) : (
    <TokenBalanceSection claimStyle={false}>
      <BalanceAmount>{!kdkBalance ? 0 : formatAmount(kdkBalance.toExact())}</BalanceAmount>
      <TokenSymbol>xKDK</TokenSymbol>
    </TokenBalanceSection>
  )
}

function Web3StatusInner() {
  const { account, chainId } = useConnection()
  const { ENSName } = useENSName(account) ?? undefined

  const allTransactions = useAllTransactions()

  const sortedRecentTransactions = useMemo(() => {
    const txs = Object.values(allTransactions)
    return txs.filter(isTransactionRecent).sort(newTransactionsFirst)
  }, [allTransactions])

  const pending = sortedRecentTransactions.filter((tx) => !tx.receipt).map((tx) => tx.hash)

  const hasPendingTransactions = pending.length > 0
  const toggleWalletModal = useToggleWalletModal()

  const selectChain = useSelectChain()

  const isAllowedChain = chainId != null && isSupportedChain(chainId)

  const displayName = useMemo(() => {
    return account ? formatDisplayName(ENSName, account) : ''
  }, [ENSName, account])

  async function handleWrongNetwork() {
    selectChain(defaultChainId())
  }

  if (account && isAllowedChain) {
    return (
      <>
        <Web3StatusConnected
          data-testid="web3-status-connected"
          onClick={toggleWalletModal}
          pending={hasPendingTransactions}
        >
          {hasPendingTransactions ? (
            <RowBetween style={{ gap: '8px' }}>
              <Text>{pending.length} Pending</Text> <Loader stroke="white" />
            </RowBetween>
          ) : (
            <ConnectedContent>
              {SHOW_KDK_BALANCE_SECTION && <KdkBalanceSection />}

              <WalletSection>
                <WalletIcon />
                <AddressText>{displayName}</AddressText>
              </WalletSection>
            </ConnectedContent>
          )}
        </Web3StatusConnected>
      </>
    )
  }
  if (!account) {
    return (
      <Web3StatusConnect onClick={toggleWalletModal} faded>
        <Text>
          <>Connect Wallet</>
        </Text>
      </Web3StatusConnect>
    )
  }
  if (account && !isAllowedChain) {
    return (
      <Web3StatusConnect onClick={handleWrongNetwork}>
        <Text>
          <>Wrong Network</>
        </Text>
      </Web3StatusConnect>
    )
  } else {
    return null
  }
}

export default function Web3Status() {
  const allTransactions = useAllTransactions()

  const { pending, confirmed } = useMemo(() => {
    const txs = Object.values(allTransactions)
    const recent = txs.filter(isTransactionRecent).sort(newTransactionsFirst)
    return {
      pending: recent.filter((tx) => !tx.receipt).map((tx) => tx.hash),
      confirmed: recent.filter((tx) => tx.receipt).map((tx) => tx.hash),
    }
  }, [allTransactions])

  return (
    <>
      <Web3StatusInner />
      <WalletModal pendingTransactions={pending} confirmedTransactions={confirmed} />
    </>
  )
}
